import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GlobalTableNoPagination from '../../../../../components/CustomTableWithPagination/GlobalTableNoPagination';
import NoDataFound from '../../../../../components/NodataIndication/NoDataFound';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getPerformanceBreakdown } from '../../../../../redux/features/poinTrackerSlice';
import { useRef } from 'react';

export default function PerformanceTable({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.performanceBreakdown);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (selectedTeam) {
      dispatch(getPerformanceBreakdown(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const apiData = responseData?.data || [];
  const months = responseData?.meta?.months || [];

  //remove loader when change date only trigger team change
  const prevTeamRef = useRef(selectedTeam?.id);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    if (prevTeamRef.current !== selectedTeam?.id) {
      setShowLoader(true); //  show loader only for team change
    } else {
      setShowLoader(false); //  hide loader for date change
    }

    prevTeamRef.current = selectedTeam?.id;
  }, [selectedTeam?.id, fromDate, toDate]);

  const formatteddata = useMemo(() => {
    return apiData.map((item) => {
      const row = {
        id: item.slNo,
        name: item.name,
        totalCollection: item.grandTotalCollection,
        totalSales: item.grandTotalSales,
      };

      //dynamic month mapping
      (item.metrics || []).forEach((metric) => {
        const monthKey = metric.month.toLowerCase(); //april,march...

        row[`${monthKey}Sales`] = metric.sales;
        row[`${monthKey}Collection`] = metric.collection;
      });
      return row;
    });
  }, [apiData]);

  const dynamicHeaders = useMemo(() => {
    const baseHeaders = [
      { field: 'id', headerName: 'SL No', width: 100 },
      { field: 'name', headerName: 'OGA Name', width: 200 },
      { field: 'totalCollection', headerName: 'Total Collection', width: 160 },
      { field: 'totalSales', headerName: 'Total Sales', width: 160 },
    ];

    const monthHeaders = months.flatMap((m) => {
      const key = m.label.toLowerCase();

      return [
        {
          field: `${key}Sales`,
          headerName: `${m.label} Sales`,
          width: 140,
        },
        {
          field: `${key}Collection`,
          headerName: `${m.label} Collection`,
          width: 160,
        },
      ];
    });

    return [...baseHeaders, ...monthHeaders];
  }, [months]);

  const noDataFound = !loading && formatteddata.length === 0;

  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
      {/* HEADER */}
      <Box
        sx={{
          p: 2,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: 'linear-gradient(135deg, #5f2eea, #a450f5)',
        }}
      >
        <GroupsIcon />
        <Typography variant="h6">Performance Breakdown</Typography>
      </Box>

      {/* BODY */}
      {noDataFound && !loading ? (
        //  NO DATA
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '30vh',
          }}
        >
          <NoDataFound />
        </Box>
      ) : (
        //  TABLE
        <GlobalTableNoPagination
          rowUniqueId="id"
          headers={dynamicHeaders}
          tableState={tableState}
          setTableState={setTableState}
          // loading={loading}
          loading={showLoader && loading}
          rows={formatteddata}
        />
      )}
    </Card>
  );
}
